import { useCallback, useMemo } from "react";
import { useGetNationalityList } from "@/api/services/global/master";
import { useGetProvinceAndMunicipalityList } from "@/hooks/useGetProvinceAndMunicipalityList";
import { truncateWithEllipsis } from "@/utils/string";
import { TOption } from "@/types/common";
import { FILTER_LABEL_MAX_LENGTH } from "@/features/partner/components/partner-list/partner-filter-shared";

const truncateOpts = (options: TOption[]): TOption[] =>
  options.map((opt) => ({ ...opt, label: truncateWithEllipsis(opt.label, FILTER_LABEL_MAX_LENGTH) }));

export const usePartnerFilterMasterData = () => {
  const { provinceOptions, getMunicipalityOptionsByProvince } = useGetProvinceAndMunicipalityList();
  const { nationalityOptions: rawNationalityOptions } = useGetNationalityList();

  // Map prefecture options to use label as value (backend expects prefecture name, not code)
  const prefectureOptions = useMemo<TOption[]>(
    () => truncateOpts(provinceOptions.map((opt) => ({ label: opt.label, value: opt.label }))),
    [provinceOptions],
  );

  const nationalityOptions = useMemo<TOption[]>(
    () => truncateOpts(rawNationalityOptions.map((opt) => ({ label: opt.label, value: String(opt.value) }))),
    [rawNationalityOptions],
  );

  // Get municipality options (using name as value) for multiple selected prefectures
  const getMunicipalityNameOptionsByPrefectures = useCallback(
    (prefectureNames: string[]): TOption[] => {
      const allOptions: TOption[] = [];
      for (const name of prefectureNames) {
        const province = provinceOptions.find((opt) => opt.label === name);
        if (!province) continue;
        const municipalityOptions = getMunicipalityOptionsByProvince(String(province.value));
        allOptions.push(...municipalityOptions.map((opt) => ({ label: opt.label, value: opt.label })));
      }
      return truncateOpts(allOptions);
    },
    [provinceOptions, getMunicipalityOptionsByProvince],
  );

  return { prefectureOptions, nationalityOptions, getMunicipalityNameOptionsByPrefectures };
};
